import { useState } from "react";
import { Mail, Calendar, Library, Newspaper, TrendingUp, Video, User, MessageSquare, GraduationCap, Lock, Check, Linkedin, Briefcase } from "lucide-react";
import { useNavigation } from "@/contexts/NavigationContext";

export function ConfigurationPage() {
  const { navigate } = useNavigation();
  const [emailConfig, setEmailConfig] = useState("");
  const [issueReportEmail, setIssueReportEmail] = useState("");
  const [calendlyLink, setCalendlyLink] = useState("");
  const [linkedinProfile, setLinkedinProfile] = useState("");
  const [library1, setLibrary1] = useState("");
  const [library2, setLibrary2] = useState("");
  const [newsSource, setNewsSource] = useState("");
  const [aiResearchSource, setAiResearchSource] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [jobFairSource, setJobFairSource] = useState("");
  const [avatarId, setAvatarId] = useState("");
  const [avatarContent30, setAvatarContent30] = useState("");
  const [avatarContent60, setAvatarContent60] = useState("");
  const [trainingVideos, setTrainingVideos] = useState<string[]>(["", "", "", "", "", ""]);
  const [governanceDocuments, setGovernanceDocuments] = useState<string[]>(["", "", ""]);
  const [regulatoryLink, setRegulatoryLink] = useState("");
  const [incidentResponsePlan, setIncidentResponsePlan] = useState("");
  const [infoSecurityManagement, setInfoSecurityManagement] = useState("");
  const [oversiteAuditLink1, setOversiteAuditLink1] = useState("");
  const [oversiteAuditLink2, setOversiteAuditLink2] = useState("");

  const handleTrainingVideoChange = (index: number, value: string) => {
    const newVideos = [...trainingVideos];
    newVideos[index] = value;
    setTrainingVideos(newVideos);
  };

  const handleSave = () => {
    // Save configuration to localStorage
    const config = {
      emailConfig,
      issueReportEmail,
      calendlyLink,
      linkedinProfile,
      library1,
      library2,
      newsSource,
      aiResearchSource,
      videoLink,
      videoUrl,
      jobFairSource,
      avatarId,
      avatarContent30,
      avatarContent60,
      trainingVideos,
      governanceDocuments,
      regulatoryLink,
      incidentResponsePlan,
      infoSecurityManagement,
      oversiteAuditLink1,
      oversiteAuditLink2
    };
    localStorage.setItem('oraConfiguration', JSON.stringify(config));
    alert('Configuration saved successfully!');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-black text-white py-3 px-4 sticky top-0 z-10 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-lg font-bold mb-0.5 !text-purple-600">Configuration</h1>
          <p className="text-xs text-gray-300">Real-time intelligence with customizable context!</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-3 py-4">
        
        {/* Subscription Required Notice */}
        <div className="mb-2">
          <p className="text-base font-bold text-green-600">Subscription Required</p>
        </div>
        
        {/* Premium Features List */}
        <div className="mb-4 bg-white rounded-lg p-3">
          <h3 className="text-sm font-bold !text-black mb-1.5">Premium Features Included:</h3>
          <ul className="space-y-0.5">
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Real-time intelligence with custom context!</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">AI leadership video library access</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Shareable agent link with unlimited shares</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Custom AI avatar creation (15 seconds Intro)</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Your persona video based on existing AI avatar (30-45 seconds with your script)</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Unlimited questions</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Google Drive / SharePoint integration</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Connects with LinkedIn and Calendly and Facetime or Teams or Zoom</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Complete data privacy</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Advanced analytics</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Option to Upgrade and build your own integrated AI persona!</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">Option to include 1 video url</span>
            </li>
            <li className="flex items-start gap-1.5 text-xs leading-tight">
              <span className="text-purple-600 mt-0.5">✓</span>
              <span className="!text-black">AI trend rag from select source</span>
            </li>
          </ul>
        </div>
        
        {/* User Email Configuration */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Mail className="size-4" />
            <h2 className="text-sm font-bold text-black">User Email <span className="text-red-500">*</span></h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Configure the user email address</p>
          <input
            type="email"
            value={emailConfig}
            onChange={(e) => setEmailConfig(e.target.value)}
            placeholder="email@example.com"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black mb-3 text-sm"
          />
          
          <label className="text-xs font-semibold text-black mb-1.5 block">Email address for issue reporting (recommended)</label>
          <input
            type="email"
            value={issueReportEmail}
            onChange={(e) => setIssueReportEmail(e.target.value)}
            placeholder="issue-reports@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* Governance & Security Section */}
        <div className="mb-4 pb-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg p-3">
          <h2 className="text-sm font-bold text-black mb-3">Governance & Security</h2>
          
          {/* AI Documents Library (SOPs and Governance) */}
          <div className="mb-3">
            <h3 className="text-xs font-semibold text-black mb-1.5">AI Documents Library (SOPs and Governance) - up to 3</h3>
            <p className="text-xs text-gray-600 mb-2">Add links to AI-related documents, SOPs, policies, and governance resources</p>
            <div className="space-y-2">
              {governanceDocuments.map((url, index) => (
                <input
                  key={index}
                  type="url"
                  value={url}
                  onChange={(e) => {
                    const newDocs = [...governanceDocuments];
                    newDocs[index] = e.target.value;
                    setGovernanceDocuments(newDocs);
                  }}
                  placeholder={`AI Document ${index + 1} URL`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
                />
              ))}
            </div>
          </div>

          {/* Regulatory Link */}
          <div className="mb-3">
            <h3 className="text-xs font-semibold text-black mb-1.5">Regulatory Link</h3>
            <p className="text-xs text-gray-600 mb-2">Add link to regulatory compliance resources and guidelines</p>
            <input
              type="url"
              value={regulatoryLink}
              onChange={(e) => setRegulatoryLink(e.target.value)}
              placeholder="Regulatory compliance URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
            />
          </div>

          {/* Incident Response Plan */}
          <div className="mb-3">
            <h3 className="text-xs font-semibold text-black mb-1.5">Incident Response Plan</h3>
            <p className="text-xs text-gray-600 mb-2">Add link to your incident response plan</p>
            <input
              type="url"
              value={incidentResponsePlan}
              onChange={(e) => setIncidentResponsePlan(e.target.value)}
              placeholder="Incident response plan URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
            />
          </div>

          {/* Information Security Management */}
          <div className="mb-3">
            <h3 className="text-xs font-semibold text-black mb-1.5">Information Security Management</h3>
            <p className="text-xs text-gray-600 mb-2">Add link to your information security management policy</p>
            <input
              type="url"
              value={infoSecurityManagement}
              onChange={(e) => setInfoSecurityManagement(e.target.value)}
              placeholder="Information security management URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
            />
          </div>

          {/* Oversite & Auditing */}
          <div className="mb-3">
            <h3 className="text-xs font-semibold text-black mb-1.5">Oversite & Auditing</h3>
            <p className="text-xs text-gray-600 mb-2">Add links to oversight and auditing resources</p>
            <div className="space-y-2">
              <input
                type="url"
                value={oversiteAuditLink1}
                onChange={(e) => setOversiteAuditLink1(e.target.value)}
                placeholder="Oversight & Auditing Link 1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
              />
              <input
                type="url"
                value={oversiteAuditLink2}
                onChange={(e) => setOversiteAuditLink2(e.target.value)}
                placeholder="Oversight & Auditing Link 2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
              />
            </div>
          </div>

          {/* Video Meeting */}
          <div>
            <h3 className="text-xs font-semibold text-black mb-1.5">Quarterly Video Meeting for Governance Committee Oversight</h3>
            <p className="text-xs text-gray-600 mb-2">Configure video meeting link</p>
            <input
              type="url"
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
              placeholder="Video meeting URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
            />
          </div>
        </div>
        
        {/* Avatar ID */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <User className="size-4" />
            <h2 className="text-sm font-bold text-black">Avatar ID</h2>
          </div>
          <p className="text-xs text-gray-600 mb-3">Configure your avatar identifier and assets</p>
          
          <div className="space-y-3">
            {/* Avatar ID Input */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">Avatar Identifier Name</label>
              <input
                type="text"
                value={avatarId}
                onChange={(e) => setAvatarId(e.target.value)}
                placeholder="Enter Avatar ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
              />
              <p className="text-[10px] text-gray-500 mt-1">(this should be about 5 letters and memorable)</p>
            </div>

            {/* Profile Image Upload */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">Profile Image</label>
              <input
                type="file"
                accept="image/*"
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-black hover:file:bg-gray-200"
              />
              <div className="mt-2 p-2 bg-gray-50 border border-gray-300 rounded-lg text-black">
                <p className="text-[10px] font-semibold mb-1 text-black">⚠️ Image Requirements:</p>
                <ul className="text-[10px] space-y-0.5 ml-3 list-disc text-black">
                  <li className="text-black">High-quality image facing forward</li>
                  <li className="text-black">Must include torso with arms visible</li>
                  <li className="text-black">Clear, well-lit photograph</li>
                  <li className="text-black">Professional appearance recommended</li>
                </ul>
              </div>
            </div>

            {/* Voice Sample Upload */}
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">Voice Sample (30 seconds)</label>
              <input
                type="file"
                accept="audio/*"
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-black hover:file:bg-gray-200"
              />
              <p className="text-[10px] text-gray-500 mt-1">Upload a 30-second audio sample for voice training</p>
            </div>
          </div>
        </div>

        {/* Avatar Video Content */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <MessageSquare className="size-4" />
            <h2 className="text-sm font-bold text-black">Avatar Video Content</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Configure content for avatar videos</p>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">30-45 words</label>
              <textarea
                value={avatarContent30}
                onChange={(e) => setAvatarContent30(e.target.value)}
                placeholder="Enter content (30-45 words)"
                maxLength={280}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black resize-none h-20 text-sm"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                {avatarContent30.split(' ').filter(w => w).length} words
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-black mb-1.5 block">Persona script: 60-70 words</label>
              <textarea
                value={avatarContent60}
                onChange={(e) => setAvatarContent60(e.target.value)}
                placeholder="Enter content (60-70 words)"
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black resize-none h-24 text-sm"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                {avatarContent60.split(' ').filter(w => w).length} words
              </div>
            </div>
          </div>
        </div>

        {/* Training Video Content */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <GraduationCap className="size-4" />
            <h2 className="text-sm font-bold text-black">Training Video Content</h2>
            <span className="ml-auto flex items-center gap-0.5 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
              <Lock className="size-2.5" />
              Premium
            </span>
          </div>
          <p className="text-xs text-gray-600 mb-2">Configure up to 6 training videos (Premium feature)</p>
          
          <div className="space-y-2 opacity-60">
            {trainingVideos.map((video, index) => (
              <div key={index} className="relative">
                <input
                  type="url"
                  value={video}
                  onChange={(e) => handleTrainingVideoChange(index, e.target.value)}
                  placeholder={`Training Video ${index + 1} URL`}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black bg-gray-50 cursor-not-allowed text-sm"
                />
              </div>
            ))}
          </div>

          <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-black mb-1 font-semibold">Upgrade to Premium</p>
            <p className="text-[10px] text-gray-600 mb-2">Unlock training video configuration and more advanced features</p>
            <a
              href="https://agent.myora.now/?view=pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors inline-flex items-center justify-center no-underline hover:no-underline"
              style={{ color: '#ffffff' }}
            >
              View Pricing Plans
            </a>
          </div>
        </div>

        {/* Calendly Configuration */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Calendar className="size-4" />
            <h2 className="text-sm font-bold text-black">Calendly Integration</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Add your Calendly scheduling link</p>
          <input
            type="url"
            value={calendlyLink}
            onChange={(e) => setCalendlyLink(e.target.value)}
            placeholder="https://calendly.com/your-link"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* LinkedIn Profile */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Linkedin className="size-4" />
            <h2 className="text-sm font-bold text-black">LinkedIn Profile</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Add your LinkedIn profile link</p>
          <input
            type="url"
            value={linkedinProfile}
            onChange={(e) => setLinkedinProfile(e.target.value)}
            placeholder="https://linkedin.com/in/your-profile"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* Context Libraries */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Library className="size-4" />
            <h2 className="text-sm font-bold text-black">Context Libraries (up to 2)</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Connect your Google Drive or SharePoint libraries</p>
          <div className="space-y-2">
            <input
              type="url"
              value={library1}
              onChange={(e) => setLibrary1(e.target.value)}
              placeholder="Library 1 URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
            />
            <input
              type="url"
              value={library2}
              onChange={(e) => setLibrary2(e.target.value)}
              placeholder="Library 2 URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
            />
          </div>
        </div>

        {/* Context News */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Newspaper className="size-4" />
            <h2 className="text-sm font-bold text-black">Context News</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Configure news sources for context</p>
          <input
            type="url"
            value={newsSource}
            onChange={(e) => setNewsSource(e.target.value)}
            placeholder="News source URL or RSS feed"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* Video URL */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Video className="size-4" />
            <h2 className="text-sm font-bold text-black">Video URL</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Add a video URL for your AI agent</p>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="Video URL"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* Job Fair & Hiring Information */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <Briefcase className="size-4" />
            <h2 className="text-sm font-bold text-black">Job Fair & Hiring Information</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Add current job fair and hiring information from a source</p>
          <input
            type="url"
            value={jobFairSource}
            onChange={(e) => setJobFairSource(e.target.value)}
            placeholder="Job fair source URL"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* Context AI Research and Trends */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp className="size-4" />
            <h2 className="text-sm font-bold text-black">Context AI Research and Trends</h2>
          </div>
          <p className="text-xs text-gray-600 mb-2">Configure AI research and trends sources</p>
          <input
            type="url"
            value={aiResearchSource}
            onChange={(e) => setAiResearchSource(e.target.value)}
            placeholder="AI research source URL"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black text-sm"
          />
        </div>

        {/* Save Button */}
        <div className="sticky bottom-0 bg-white pt-4 pb-3 border-t border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex-1 bg-green-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <Check className="size-4" />
              Save Configuration
            </button>
            <button
              onClick={() => navigate('/unified-agent')}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors text-black"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Free Trial Agreement Link */}
        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-600">
            By using ORA, you agree to our{" "}
            <button 
              onClick={() => navigate('/agreement')}
              className="text-black font-semibold underline hover:text-gray-700 transition-colors"
            >
              Free Trial User Agreement
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}